/**
 * TopBar.jsx
 * Minimal premium top bar.
 * Left: page title + date
 * Right: New Booking, Notifications, Theme toggle, User dropdown
 */
import React, { useState, useRef, useEffect } from "react";
import { Sun, Moon, Bell, Plus, Menu, ChevronDown, LogOut, Building2, User } from "lucide-react";
import { useTheme } from "./ThemeProvider";

const PAGE_TITLES = {
  dashboard:  "Dashboard",
  monitor:    "Daily Monitor",
  calendar:   "Calendar",
  analytics:  "Analytics",
  finance:    "Finance",
  properties: "Properties",
  activity:   "Activity Log",
  requests:   "Join Requests",
  settings:   "Settings",
  add:        "New Booking",
};

const TODAY = new Date().toLocaleDateString("en-PK", {
  weekday: "long", day: "numeric", month: "long", year: "numeric",
});

export default function TopBar({
  activeTab,
  currentUser,
  isAdmin,
  pendingCount,
  upcomingCount,
  onNewBooking,
  onLogout,
  onMobileMenu,
  onSwitchBusiness,
  businessName,
}) {
  const { theme, toggle } = useTheme();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notifOpen,    setNotifOpen]    = useState(false);
  const dropRef  = useRef(null);
  const notifRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current  && !dropRef.current.contains(e.target))  setDropdownOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <header className="topbar">
      {/* Left */}
      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
        {/* Mobile hamburger */}
        <button className="icon-btn" onClick={onMobileMenu}
          style={{ display:"none" }} id="mobile-menu-btn">
          <Menu size={18} />
        </button>

        <div className="topbar-left">
          <div className="topbar-title">{PAGE_TITLES[activeTab] || "StayTrack"}</div>
          <div className="topbar-date">{TODAY}</div>
        </div>
      </div>

      {/* Right actions */}
      <div className="topbar-right">

        {/* New Booking button */}
        <button
          className="st-btn st-btn-primary st-btn-sm"
          onClick={onNewBooking}
          style={{ gap:6 }}
        >
          <Plus size={15} strokeWidth={2.5} />
          <span>New Booking</span>
        </button>

        {/* Notifications */}
        <div className="dropdown" ref={notifRef}>
          <button
            className="icon-btn"
            onClick={() => setNotifOpen(p => !p)}
            style={{ position:"relative" }}
            title="Notifications"
          >
            <Bell size={17} />
            {(pendingCount > 0 || upcomingCount > 0) && (
              <span style={{
                position:"absolute", top:4, right:4,
                width:8, height:8, borderRadius:"50%",
                background:"var(--danger)",
                border:"2px solid var(--card)",
              }} />
            )}
          </button>

          {notifOpen && (
            <div className="dropdown-menu" style={{ width:300 }}>
              <div style={{ padding:"12px 16px", borderBottom:"1px solid var(--border)" }}>
                <p style={{ fontSize:13, fontWeight:700 }}>Notifications</p>
              </div>
              {pendingCount === 0 && upcomingCount === 0 ? (
                <div style={{ padding:"20px 16px", textAlign:"center", color:"var(--text-2)", fontSize:13 }}>
                  All clear! No notifications.
                </div>
              ) : (
                <div style={{ padding:"8px 0" }}>
                  {upcomingCount > 0 && (
                    <div className="dropdown-item">
                      <span style={{ fontSize:16 }}>📅</span>
                      <div>
                        <p style={{ fontSize:13, fontWeight:600 }}>{upcomingCount} check-in{upcomingCount>1?"s":""} soon</p>
                        <p style={{ fontSize:11, color:"var(--text-2)" }}>Within the next 3 days</p>
                      </div>
                    </div>
                  )}
                  {pendingCount > 0 && (
                    <div className="dropdown-item">
                      <span style={{ fontSize:16 }}>🔔</span>
                      <div>
                        <p style={{ fontSize:13, fontWeight:600 }}>{pendingCount} join request{pendingCount>1?"s":""}</p>
                        <p style={{ fontSize:11, color:"var(--text-2)" }}>Waiting for approval</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Theme toggle */}
        <button
          className="icon-btn"
          onClick={toggle}
          title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        >
          {theme === "dark"
            ? <Sun  size={17} strokeWidth={1.8} />
            : <Moon size={17} strokeWidth={1.8} />
          }
        </button>

        {/* User dropdown */}
        <div className="dropdown" ref={dropRef}>
          <button
            onClick={() => setDropdownOpen(p => !p)}
            style={{
              display:"flex", alignItems:"center", gap:8,
              padding:"6px 10px", borderRadius:10,
              border:"1px solid var(--border)",
              background:"var(--card)",
              cursor:"pointer", transition:"all 0.15s",
              fontFamily:"Inter, sans-serif",
            }}
            onMouseEnter={e => e.currentTarget.style.background="var(--hover)"}
            onMouseLeave={e => e.currentTarget.style.background="var(--card)"}
          >
            {/* Avatar */}
            <div style={{
              width:28, height:28, borderRadius:8,
              background:"linear-gradient(135deg,#F5B841,#F0A800)",
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:13, fontWeight:800, color:"#111",
              flexShrink:0,
            }}>
              {currentUser?.[0]?.toUpperCase() || "U"}
            </div>
            <div style={{ textAlign:"left" }}>
              <p style={{ fontSize:13, fontWeight:600, color:"var(--text)", lineHeight:1.1 }}>
                {currentUser}
              </p>
              {isAdmin && (
                <p style={{ fontSize:10, color:"var(--gold)", fontWeight:600, lineHeight:1 }}>Admin</p>
              )}
            </div>
            <ChevronDown size={14} color="var(--text-2)"
              style={{ transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)", transition:"transform 0.15s" }} />
          </button>

          {dropdownOpen && (
            <div className="dropdown-menu">
              <div style={{ padding:"12px 14px 8px" }}>
                <p style={{ fontSize:12, fontWeight:700, color:"var(--text)" }}>{currentUser}</p>
                <p style={{ fontSize:11, color:"var(--text-2)", marginTop:2 }}>
                  {isAdmin ? "Administrator" : "Member"} · {businessName}
                </p>
              </div>
              <div className="dropdown-divider" />
              <button className="dropdown-item" onClick={() => { onSwitchBusiness(); setDropdownOpen(false); }}>
                <Building2 size={15} /> Switch Business
              </button>
              <div className="dropdown-divider" />
              <button className="dropdown-item danger" onClick={onLogout}>
                <LogOut size={15} /> Sign Out
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile menu button show on small screens */}
      <style>{`
        @media (max-width: 1024px) {
          #mobile-menu-btn { display: flex !important; }
        }
      `}</style>
    </header>
  );
}
